// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ChargeListItem } from '@/billing/api/types';
import {
  DoNotDisturbAltOutlined,
  HistoryRounded,
  ReceiptLongRounded,
  Restore,
  ScheduleOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';

interface ChargeActionMenuProps {
  menuAnchor: HTMLElement | null;
  menuCharge: ChargeListItem | null;
  onClose: () => void;
  onViewBillingPlan: () => void;
  onViewAuditLog: () => void;
  onCancelCharge: () => void;
  onMarkBillable: () => void;
  onMarkNotBillable: () => void;
  onRevertCancellation: () => void;
}

export function ChargeActionMenu(props: ChargeActionMenuProps) {
  const navigate = useNavigate();

  return (
    <Menu
      anchorEl={props.menuAnchor}
      open={Boolean(props.menuAnchor)}
      onClose={props.onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      {props.menuCharge?.status === 'PLANNED' && [
        // PERIODIC charges can only be billed once their line item is
        // CONFIRMED. MILESTONE charges flow through because the action
        // IS the activation event. Mirrors the BE 3710 guard in
        // MarkChargeBillable.
        props.menuCharge.lineItemChargeMode === 'PENDING' &&
        props.menuCharge.chargeSchedule !== 'MILESTONE' ? (
          <Tooltip
            key="mark-billable-disabled"
            arrow
            title="Billing plan needs to be activated first"
            placement="left"
          >
            <span>
              <MenuItem disabled>
                <ListItemIcon>
                  <ReceiptLongRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText>Mark billable</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        ) : (
          <MenuItem key="mark-billable" onClick={props.onMarkBillable}>
            <ListItemIcon>
              <ReceiptLongRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mark billable</ListItemText>
          </MenuItem>
        ),
        <MenuItem key="view-plan" onClick={props.onViewBillingPlan}>
          <ListItemIcon>
            <ScheduleOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>View related billing plan</ListItemText>
        </MenuItem>,
        <MenuItem key="audit-log" onClick={props.onViewAuditLog}>
          <ListItemIcon>
            <HistoryRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>View audit log</ListItemText>
        </MenuItem>,
        <MenuItem
          key="cancel"
          onClick={props.onCancelCharge}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}>
            <DoNotDisturbAltOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel this charge</ListItemText>
        </MenuItem>,
      ]}

      {props.menuCharge?.status === 'BILLABLE' && [
        <MenuItem key="mark-not-billable" onClick={props.onMarkNotBillable}>
          <ListItemIcon>
            <Restore fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark not billable</ListItemText>
        </MenuItem>,
        <MenuItem key="view-plan" onClick={props.onViewBillingPlan}>
          <ListItemIcon>
            <ScheduleOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>View related billing plan</ListItemText>
        </MenuItem>,
        <MenuItem key="audit-log" onClick={props.onViewAuditLog}>
          <ListItemIcon>
            <HistoryRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>View audit log</ListItemText>
        </MenuItem>,
        <MenuItem
          key="cancel"
          onClick={props.onCancelCharge}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}>
            <DoNotDisturbAltOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel this charge</ListItemText>
        </MenuItem>,
      ]}

      {props.menuCharge?.status === 'INVOICED' && [
        <MenuItem
          key="view-invoice"
          disabled={!props.menuCharge.invoiceKey}
          onClick={() => {
            if (props.menuCharge?.invoiceKey) {
              props.onClose();
              navigate({
                to: '/billing/invoices/$invoiceKey',
                params: { invoiceKey: props.menuCharge.invoiceKey },
              });
            }
          }}
        >
          <ListItemIcon>
            <ReceiptLongRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>View invoice</ListItemText>
        </MenuItem>,
        <MenuItem key="view-plan" onClick={props.onViewBillingPlan}>
          <ListItemIcon>
            <ScheduleOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>View related billing plan</ListItemText>
        </MenuItem>,
        <MenuItem key="audit-log" onClick={props.onViewAuditLog}>
          <ListItemIcon>
            <HistoryRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>View audit log</ListItemText>
        </MenuItem>,
        <Tooltip
          key="mark-not-billable-disabled"
          arrow
          title="This charge must be removed from the invoice in order to be marked not billable"
          placement="left"
        >
          <span>
            <MenuItem disabled>
              <ListItemIcon>
                <Restore fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mark not billable</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>,
        <Tooltip
          key="cancel-disabled"
          arrow
          title="This charge must be removed from the invoice in order to be cancelled"
          placement="left"
        >
          <span>
            <MenuItem disabled>
              <ListItemIcon>
                <DoNotDisturbAltOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cancel this charge</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>,
      ]}

      {props.menuCharge?.status === 'CANCELLED' && [
        <MenuItem
          key="revert-cancellation"
          onClick={props.onRevertCancellation}
        >
          <ListItemIcon>
            <Restore fontSize="small" />
          </ListItemIcon>
          <ListItemText>Revert cancellation</ListItemText>
        </MenuItem>,
        <MenuItem key="view-plan" onClick={props.onViewBillingPlan}>
          <ListItemIcon>
            <ScheduleOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>View related billing plan</ListItemText>
        </MenuItem>,
        <MenuItem key="audit-log" onClick={props.onViewAuditLog}>
          <ListItemIcon>
            <HistoryRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>View audit log</ListItemText>
        </MenuItem>,
      ]}
    </Menu>
  );
}
